import { nanoid } from '@reduxjs/toolkit';
import { Decimal } from 'decimal.js';
import { Cart, CartDelivery, CartItem, CartItemData } from './';
import { DeliveryType } from './types';

import { AddItem } from './actions/cart';

/** Find an existing deliveryId for a given type and locationId */
export function findDelivery(
  cart: Cart,
  {
    locationId,
    type,
    withoutId,
  }: { locationId: number; type: DeliveryType; withoutId?: number },
): string | null {
  const match = Object.entries(cart.deliveries).find(([, delivery]) => {
    if (delivery.type === type && delivery.locationId === locationId) {
      if (!withoutId || !delivery.items[withoutId]) {
        return true;
      }
      return false;
    }
    return false;
  });
  return match ? match[0] : null;
}

/** Initialize a new cartItem */
export function populateCartItem(
  itemData: CartItemData,
  { amount, price: a }: { amount: number; price?: number },
): CartItem {
  /** use the provided price (retailer app) or use the stock price from the article */
  const price = a ?? itemData.basePrice;

  const lineTotalNet = new Decimal(price).mul(new Decimal(amount)).toNumber();
  const lineTotalQuantity = new Decimal(itemData.quantity)
    .mul(new Decimal(amount))
    .toNumber();

  // (itemData.taxRate / 100) * lineTotalNet;
  const lineTax = new Decimal(itemData.taxRate)
    .div(new Decimal(100))
    .mul(new Decimal(lineTotalNet))
    .toNumber();

  return {
    itemData,
    amount,
    price,
    lineTax,
    lineTotalNet,
    lineTotalGross: lineTotalNet + lineTax,
    lineTotalQuantity,
    addedAt: Date.now(), // Add timestamp when item is created
  };
}

type CartValidation = {
  pricesForAllItems: boolean;
  totalValue?: number;
};

export function sumResults(
  previous: CartValidation,
  current: CartValidation,
): CartValidation {
  return {
    pricesForAllItems: previous.pricesForAllItems && current.pricesForAllItems,
    totalValue: (previous.totalValue || 0) + (current.totalValue || 0),
  };
}

export function sumDelivery(delivery: CartDelivery): CartValidation {
  return Object.values(delivery.items).reduce<CartValidation>(
    (sum, item) => ({
      pricesForAllItems: sum.pricesForAllItems && !!item.lineTotalGross,
      totalValue: (sum.totalValue || 0) + (item.lineTotalGross || 0),
    }),
    {
      pricesForAllItems: true,
      totalValue: 0,
    },
  );
}

export const initSumResults = {
  pricesForAllItems: true,
};

/** Payload type for creating or adding items to a delivery */
export type DeliveryPayload = Pick<
  AddItem,
  'itemData' | 'delivery' | 'amount' | 'locationId' | 'price'
> & {
  requiredDeliveryDate?: boolean;
  type?: DeliveryType;
};

/** Helper to initialize a new delivery for a given article */
export function createNewDelivery(payload: DeliveryPayload): CartDelivery {
  const deliveryType: DeliveryType =
    payload.type ??
    payload.delivery?.type ??
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    (payload.itemData.quantityOptions?.default?.toUpperCase() as DeliveryType) ??
    DeliveryType.Delivery;
  const delivery: CartDelivery = {
    id: nanoid(),

    /** use the articles recommended delivery type or default it */
    type: deliveryType,

    locationId:
      payload.locationId ?? // use the chosen location
      payload.itemData.defaultLocation ?? // or use the set default location
      payload.itemData.locations[0].id, // just take any location

    deliveryAddress: payload.delivery?.deliveryAddress || null, // this will need to get set elsewhere before submitting
    isValid:
      (!!payload.delivery?.deliveryAddress || // valid is we have a delivery address or the order is for pickup
        deliveryType === DeliveryType.Pickup) &&
      !payload.delivery?.siloId && // valid if theres no siloID (as we need delivery dates to be added if it's for Silo)
      !payload.requiredDeliveryDate, // invalid if requiredDeliveryDate is set to true (as we ened delivery dates when its true)
    isReOrder: payload.delivery?.isReOrder,
    siloId: payload.delivery?.siloId,
    comment: payload.delivery?.comment,

    items: {
      [payload.itemData.id]: populateCartItem(payload.itemData, {
        amount: payload.amount,
        price: payload.price ?? payload.itemData.basePrice,
      }),
    },
  };

  return delivery;
}

/** Just adds the item to an existing delivery */
export function addToDelivery(
  delivery: CartDelivery,
  payload: AddItem,
): CartDelivery {
  const newDelivery = { ...delivery };

  newDelivery.items = {
    ...delivery.items,
    [payload.itemData.id]: populateCartItem(payload.itemData, {
      amount: payload.amount,
      price: payload.price ?? payload.itemData.basePrice,
    }),
  };

  return newDelivery;
}
